/**
 * analytics.ts — GA4 event tracking helpers for InviteCloud.
 *
 * Design rules:
 *   - All gtag() calls go through this module. Never call window.gtag() directly
 *     from components — that makes tracking impossible to grep/audit/test.
 *   - Uses the native `arguments` object (NOT spread `...args`) when pushing to
 *     dataLayer. GA4 silently drops events if you push a plain Array instead of
 *     the Arguments object.
 *   - All functions are no-ops when window.gtag is not loaded (dev / test / blocked).
 *   - Measurement ID is read from VITE_GA_MEASUREMENT_ID at runtime.
 *
 * Usage:
 *   import { trackEvent, trackPageView } from '@/src/infrastructure/lib/analytics';
 *   trackEvent('INVITATION_CREATED', 'invitation', 'wedding');
 */

// ── Type declarations ──────────────────────────────────────────────────────

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

// ── Product event names (matches requirements doc) ─────────────────────────

export type ProductEventName =
  | 'LOGIN'
  | 'LOGOUT'
  | 'DASHBOARD_VIEW'
  | 'TEMPLATE_VIEW'
  | 'TEMPLATE_SELECTED'
  | 'INVITATION_CREATED'
  | 'INVITATION_PUBLISHED'
  | 'RSVP_SUBMITTED'
  | 'MEDIA_UPLOADED'
  | 'SHARE_LINK_GENERATED'
  | 'QR_DOWNLOADED'
  | 'PDF_DOWNLOADED'
  | 'SETTINGS_VIEWED'
  | 'PRICING_VIEWED'
  | 'UPGRADE_CLICKED'
  // PAYMENT_SUCCESS and RSVP_LANDING_VIEW are declared but not fired yet: the admin dashboard shows them as
  // "Not tracked yet" via UNTRACKED_EVENTS (InviteCloudBackend google-analytics.service.ts). When you START
  // firing one, remove it from that list in the same PR.
  | 'PAYMENT_SUCCESS'
  | 'GUEST_INVITATION_VIEW'
  | 'RSVP_LANDING_VIEW'
  | 'RSVP_CONVERTED_TO_USER'
  /** Compliance: fired when user accepts platform policies. Backed by server audit log. */
  | 'POLICY_ACCEPTED'
  | 'POLICY_AGREEMENT_RECORDED'
  // ── Behaviour analytics (landing clicks, scroll/dwell, wizard timing, template changes) ──
  // Params for each are registered as custom dimensions/metrics: see docs/analytics/GA4_SETUP.md.
  | 'PUBLIC_CLICK'
  | 'DEAD_CLICK'
  | 'SCROLL_DEPTH'
  | 'SECTION_DWELL'
  | 'WIZARD_STARTED'
  | 'WIZARD_STEP_EXIT'
  | 'WIZARD_FIELD_TIME'
  | 'TEMPLATE_CHANGED'
  | 'TEMPLATE_CHANGE_CANCELLED';

/**
 * GA4 event parameters that are registered as custom dimensions / metrics on the property.
 * An unregistered parameter is still collected (DebugView, BigQuery) but is invisible to reports
 * and to the Data API — so add new names HERE and in docs/analytics/GA4_SETUP.md together.
 */
export type GaParamName =
  // dimensions (strings)
  | 'template_id' | 'occasion_type' | 'plan_name' | 'status'
  | 'element_id' | 'section' | 'click_zone' | 'scroll_percent'
  | 'step_name' | 'field_name' | 'exit_type' | 'from_template_id' | 'to_template_id'
  // metrics (numbers)
  | 'duration_sec' | 'wizard_total_sec' | 'template_changes';

export type GaParams = Partial<Record<GaParamName, string | number>>;

// ── Internal helper ────────────────────────────────────────────────────────

/** Hosts allowed to send analytics. Override with VITE_GA_ALLOWED_HOSTS (comma-separated). */
const DEFAULT_ANALYTICS_HOSTS = ['invitecloud.in', 'www.invitecloud.in'];

export function isAnalyticsHost(hostname: string): boolean {
  const override = (import.meta.env.VITE_GA_ALLOWED_HOSTS as string | undefined)?.trim();
  const allowed = override ? override.split(',').map((h) => h.trim()) : DEFAULT_ANALYTICS_HOSTS;
  return allowed.includes(hostname);
}

function isGtagAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

// `pagehide` runs while document.visibilityState is often still "visible" (Safari/Firefox), so flushes
// sent from pagehide handlers would go out as normal requests and be lost on unload. Track closing
// explicitly (capture phase, registered at import so it runs before any page-level handler).
let pageClosing = false;
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => { pageClosing = true; }, true);
  window.addEventListener('pageshow', () => { pageClosing = false; });
}

/** True when GA4 is loaded. Behaviour listeners check this so they cost nothing when analytics is off. */
export function isAnalyticsActive(): boolean {
  return isGtagAvailable();
}

// ── Bootstrap ──────────────────────────────────────────────────────────────

/**
 * Load gtag.js and configure GA4 — but ONLY when a real Measurement ID is set.
 *
 * This is done in JS (not a static <script> in index.html) so that a blank
 * VITE_GA_MEASUREMENT_ID truly disables GA4: no gtag.js request (no 404), no
 * junk `gtag('config', '%VITE_…%')`, and window.gtag stays undefined so every
 * helper below no-ops. Call once from main.tsx before React renders.
 *
 * Page views are NOT auto-sent (`send_page_view: false`) — the SPA
 * AnalyticsTracker fires page_view per route, including the initial one.
 */
export function initAnalytics(): void {
  if (typeof window === 'undefined') return;
  // Only real deployments report to GA4. localhost / *.pages.dev previews used to share the
  // production property, which made them ~60% of all "users" (Oct 2026 audit).
  if (!isAnalyticsHost(window.location.hostname)) return;
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
  // Valid GA4 IDs look like `G-XXXXXXXXXX`. Anything else (blank, the unreplaced
  // Vite placeholder) disables analytics entirely.
  if (!measurementId || !/^G-[A-Z0-9]+$/i.test(measurementId)) return;
  if (isGtagAvailable()) return; // already initialised (HMR / double-mount guard)

  window.dataLayer = window.dataLayer || [];
  // Must use the native `arguments` object — see module header.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', measurementId, { send_page_view: false });
}

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Associate the logged-in user ID with the GA4 session for cross-device tracking.
 * Call with the user's stable database ID when signed in, or null when signed out.
 */
export function setUserId(userId: string | null): void {
  if (!isGtagAvailable()) return;
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
  if (!measurementId) return;

  window.gtag('config', measurementId, {
    user_id: userId ?? undefined,
  });
}

/**
 * Track a virtual page view. Called by AnalyticsTracker on every route change, including
 * the first one (initAnalytics sets `send_page_view: false`, so nothing else sends it).
 *
 * Must be an explicit `page_view` event: re-`config`ing with page_path does NOT send a hit
 * once `send_page_view` is false, which left several routes with users but no page views.
 */
export function trackPageView(path: string, title?: string): void {
  if (!isGtagAvailable()) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title ?? document.title,
  });
}

/**
 * Track a product event. Maps to the event list in the requirements doc.
 *
 * @param name         — event name (e.g. 'INVITATION_CREATED')
 * @param category     — event category grouping (e.g. 'invitation', 'auth')
 * @param label        — optional human-readable label (e.g. occasion type)
 * @param value        — optional numeric value (e.g. payment amount in paise)
 * @param extra        — GA4 custom-dimension params registered on the property
 *                       (`template_id`, `occasion_type`, `plan_name`, `status`). Without these the
 *                       registered dimensions report "(not set)" — `label` is NOT a dimension.
 */
export function trackEvent(
  name: ProductEventName,
  category: string,
  label?: string,
  value?: number,
  extra?: GaParams,
): void {
  if (!isGtagAvailable()) return;

  const params: Record<string, unknown> = { event_category: category };
  if (label !== undefined) params.event_label = label;
  if (value !== undefined) params.value = value;
  if (extra) Object.assign(params, extra);
  // Events fired while the page is closing (dwell/step exit) must use sendBeacon or they are lost.
  if (pageClosing || (typeof document !== 'undefined' && document.visibilityState === 'hidden')) params.transport_type = 'beacon';

  window.gtag('event', name, params);
}

/**
 * Forward a Sentry exception to GA4. Used from the Sentry `beforeSend` hook
 * so that GA4 receives an `exception` event with the Sentry event ID, allowing
 * cross-referencing between GA4 funnels and Sentry error detail.
 *
 * This is called automatically by init-client.ts — do not call it manually.
 */
export function trackSentryException(description: string, sentryEventId: string): void {
  if (!isGtagAvailable()) return;

  window.gtag('event', 'exception', {
    description,
    fatal: true,
    sentry_event_id: sentryEventId,
  });
}
